import { bindCatalog } from '@shared/i18n/bind.js';
import { guardCatalog } from '@shared/i18n/dev-guard.js';
import type { Locale } from '@shared/i18n/types.js';
import type { WebCatalog } from '@shared/i18n/web-catalog-type.js';

import type { I18nContextValue } from './catalog-context.js';
import type { Namespace } from './namespaces.js';

/**
 * Стор каталога локали: что уже загружено и как это отдать React.
 *
 * ⚠️ Каталог стал ДОГРУЖАЕМЫМ, и отсюда весь этот модуль. Раньше значение контекста
 * собиралось один раз в main.tsx и больше не менялось; теперь страница может попросить
 * свой namespace, и дерево обязано перерисоваться. Стор внешний (useSyncExternalStore),
 * а не React-состояние, потому что просят namespace НЕ из компонента, а из ленивого
 * импорта маршрута — там никакого React-контекста ещё нет.
 *
 * ⚠️ Значение пересобирается целиком на каждое пополнение. Мутировать привязанное дерево
 * на месте нельзя: `useSyncExternalStore` сравнивает снимки по ссылке и не заметил бы
 * изменения, а страж каталога — это Proxy, обёрнутый вокруг конкретного объекта.
 */

interface Manifest {
  locale: Locale;
  htmlLang: string;
  direction: 'ltr';
  hash: string;
  namespaces: string[];
  formats: { locale: string; currency: string };
  /** Витрина приложения: пустой `storeUrl` означает, что приложения у деплоя нет. */
  iosApp: { storeUrl: string };
  /** То же для Google Play. */
  androidApp: { storeUrl: string };
}

let manifest: Manifest | undefined;
/**
 * Запрос манифеста, пока он в полёте.
 *
 * ⚠️ Без него была гонка, воспроизводившаяся в проде. `createBrowserRouter` выполняется
 * на ИМПОРТЕ модуля роутера, то есть до `bootCatalog()` в main.tsx, и react-router сразу
 * берётся резолвить ленивый маршрут текущего адреса. Открытый напрямую `/auth/signin` падал
 * с «ensureNamespaces before boot»: маршрут просил свои строки раньше, чем приехал
 * манифест. Теперь добор не падает, а ДОЖИДАЕТСЯ манифеста.
 */
let manifestRequest: Promise<Manifest> | undefined;
/** Сырые (непривязанные) namespace, накопленные с начала сессии. */
const raw: Record<string, unknown> = {};
let value: I18nContextValue | undefined;

const listeners = new Set<() => void>();
/** Запросы в полёте, по ключу набора: два маршрута могут попросить одно и то же разом. */
const inFlight = new Map<string, Promise<void>>();

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`${url} responded ${res.status}`);
  return (await res.json()) as T;
}

function rebuild(): void {
  if (manifest === undefined) throw new Error('i18n store: rebuild before manifest');

  const bound = bindCatalog(raw, manifest.locale) as I18nContextValue['catalog'];

  /**
   * Страж включается ТОЛЬКО в разработке. Здесь у него появилась вторая работа: поймать
   * страницу, которая читает namespace, не объявленный в PAGE_NAMESPACES. Сообщение
   * «missing key "catalog.search.placeholder"» указывает и на namespace, и на ключ.
   * Vite статически заменяет DEV на false, и в проде ветка вычищается как мёртвый код.
   */
  const catalog = import.meta.env.DEV ? guardCatalog(bound) : bound;

  value = {
    catalog,
    locale: manifest.locale,
    htmlLang: manifest.htmlLang,
    formats: manifest.formats,
    /**
     * ⚠️ `?? ''` — не защитное программирование, а совместимость с ответом сервера,
     * который старше этого поля: в проде страница живёт до первой навигации на уже
     * загруженном бандле, и пришедший без поля манифест обязан означать «приложения нет»,
     * а не падение всего каталога локали.
     */
    iosAppStoreUrl: manifest.iosApp?.storeUrl ?? '',
    androidAppStoreUrl: manifest.androidApp?.storeUrl ?? '',
  };

  for (const listener of listeners) listener();
}

export function subscribeCatalog(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Снимок для useSyncExternalStore. `undefined` до загрузки — так же в компонентных тестах. */
export function catalogSnapshot(): I18nContextValue | undefined {
  return value;
}

/**
 * Манифест: запрашивает, если ещё не запрошен, и ждёт, если уже в полёте.
 *
 * ⚠️ Запрос стартует ЗДЕСЬ, а не только в bootCatalog(), и это не перестраховка.
 * `createBrowserRouter` выполняется на импорте модуля роутера — то есть в статическом
 * графе импортов main.tsx, ДО того как отработает его `start()`. React Router сразу
 * берётся резолвить ленивый маршрут текущего адреса, и при заходе прямо на `/auth/signin`
 * добор строк случался раньше любого вызова bootCatalog(). Порядок здесь задаёт роутер,
 * а не мы, поэтому стор обязан быть к этому готов.
 */
async function requireManifest(): Promise<Manifest> {
  if (manifest !== undefined) return manifest;
  manifestRequest ??= fetchJson<Manifest>('/api/v1/i18n/manifest');
  manifest = await manifestRequest;
  return manifest;
}

/**
 * Догрузка namespace. Уже загруженные пропускаются, поэтому вызывать можно свободно —
 * повторный переход на ту же страницу сети не касается.
 */
export async function ensureNamespaces(names: readonly Namespace[]): Promise<void> {
  const ready = await requireManifest();

  const missing = [...new Set(names)].filter((ns) => !(ns in raw)).sort();
  if (missing.length === 0) return;

  const key = missing.join(',');
  const pending = inFlight.get(key);
  if (pending !== undefined) return pending;

  /**
   * ⚠️ `v=<hash>` обязателен. Сервер отдаёт набор с `immutable` на год, то есть браузер
   * не станет перепроверять ETag ВООБЩЕ. Без версии в адресе тело под этим URL никогда
   * бы не обновилось, и посетитель со старым кешем получал бы старые строки под новым
   * бандлом. Хеш берётся из манифеста, а тот идёт с `no-cache`.
   */
  const url = `/api/v1/i18n/bundle?ns=${encodeURIComponent(key)}&v=${encodeURIComponent(ready.hash)}`;

  const request = fetchJson<Record<string, unknown>>(url)
    .then((bundle) => {
      Object.assign(raw, bundle);
      rebuild();
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

/**
 * Первый заход: манифест и ядро каталога. Дальше страницы догружают своё.
 *
 * ⚠️ Привязка и страж выполняются ЗДЕСЬ, до первого рендера, а не в провайдере: main.tsx
 * оборачивает вызов в try/catch и на любой сбой — сеть, разбор JSON, неожиданный лист в
 * каталоге — показывает <BootFailure/>. Внутри дерева React тот же сбой случился бы после
 * первого рендера и в этот catch не попал бы.
 */
export async function bootCatalog(core: readonly Namespace[]): Promise<void> {
  const ready = await requireManifest();

  // Язык и направление письма ставятся здесь, а не в index.html: в разработке файл
  // отдаёт Vite сырым, без серверной подстановки.
  document.documentElement.lang = ready.htmlLang;
  document.documentElement.dir = ready.direction;

  await ensureNamespaces(core);
}

/** Только для тестов: вернуть стор в состояние «ничего не загружено». */
export function resetCatalogStoreForTests(): void {
  manifest = undefined;
  manifestRequest = undefined;
  value = undefined;
  inFlight.clear();
  for (const key of Object.keys(raw)) delete raw[key];
}

export type { WebCatalog };
